import { useState } from 'react';
import { Briefcase, ArrowRight, Check } from 'lucide-react';
import { trackConversion, trackEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

// Careers strip — homepage-only, sits between AwardsPress and FinalCta.
// Currently in "no openings" mode with a quiet notify-me capture that routes
// through the existing newsletter endpoint tagged 'careers-waitlist'.
export default function Careers() {
  const [status, setStatus] = useState('idle');
  const [email, setEmail] = useState('');

  async function onSubmit(e) {
    e.preventDefault();
    if (!email) return;
    setStatus('sending');
    try {
      const r = await fetch(`${API}/api/leads/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'careers-waitlist' }),
      });
      if (!r.ok) throw new Error(await r.text());
      setStatus('success');
      trackConversion('careers_waitlist_signup', 0, 'USD', { source: 'careers-waitlist' });
      setEmail('');
    } catch (_) {
      setStatus('error');
      trackEvent('form_error', { form: 'careers-waitlist' });
    }
  }

  return (
    <section
      className="relative px-5 md:px-10 lg:px-14 py-24 md:py-32 border-t border-white/5"
      data-testid="careers-section"
      id="careers"
    >
      <div className="flex items-center gap-4 mb-10 md:mb-14">
        <span className="w-10 h-px bg-[#FF3D2E]" />
        <p className="text-[10px] md:text-[11px] tracking-[0.3em] text-white/70 flex items-center gap-2">
          <Briefcase className="w-3 h-3" />
          07 / CAREERS
        </p>
      </div>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
        {/* Left — headline */}
        <div className="md:col-span-6">
          <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight mb-6 text-balance">
            Build with SMG.
          </h2>
          <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-md">
            We hire in tight, coordinated bursts — creative directors, agents, engineers,
            editors, and operators who want to work at the studio level. When a seat
            opens, it moves fast.
          </p>
        </div>

        {/* Right — status card */}
        <div className="md:col-span-6 md:pl-8">
          <div className="border border-white/10 bg-white/[0.02] p-8 md:p-10" data-testid="careers-status-card">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-2 h-2 rounded-full bg-white/40 animate-pulse" />
              <p className="text-[10px] tracking-[0.3em] text-white/50">CURRENT STATUS</p>
            </div>
            <h3 className="font-display text-white text-3xl md:text-4xl leading-tight tracking-tight mb-3">
              No open roles right now.
            </h3>
            <p className="text-white/60 text-sm md:text-base leading-relaxed mb-8">
              Check back periodically — the roster expands with the work. Drop your
              email below and we'll ping you the moment something opens.
            </p>

            {status === 'success' ? (
              <div className="flex items-center gap-3 text-white/85" data-testid="careers-success">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#22C55E]">
                  <Check className="w-4 h-4 text-white" />
                </span>
                <span className="text-sm">You're on the list. We'll reach out when a role fits.</span>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="careers-form">
                <input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent border-b border-white/20 focus:border-white text-white text-base py-3 outline-none transition-colors"
                  data-testid="careers-email-input"
                />
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="inline-flex items-center justify-center gap-2 min-h-[48px] bg-[#FF3D2E] hover:bg-[#ff5446] text-white text-[11px] tracking-[0.3em] uppercase px-6 py-3 transition-colors disabled:opacity-50"
                  data-testid="careers-submit"
                >
                  {status === 'sending' ? 'JOINING…' : 'NOTIFY ME'}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>
            )}

            {status === 'error' && (
              <p className="text-sm text-[#FF3D2E] mt-4">
                Something went wrong. Please try again — or email careers@smgmediagroup.com directly.
              </p>
            )}
          </div>

          <p className="text-[10px] tracking-[0.3em] text-white/35 mt-6 uppercase">
            Interested in Sports Management? Athletes and agents apply via
            {' '}
            <a href="/sports-management" className="underline hover:text-white transition-colors">
              /sports-management
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
